import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { card, h1, h2, sub, btn, RGB, Icon, ICONS, errMsg, pill } from './extUi';
import TeamStage from './stage/TeamStage';
import { S, TEAM_TINT } from './stage/StageArt';

// A team's DEFAULT stage seating (seen from the crowd). Every veto of this team starts from it;
// seating changed later for a match never changes it. Route: /admin/tournaments/:id/teams/:teamId/seating

export default function ExtTeamSeating() {
  const { t } = useTranslation();
  const { id, teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [seats, setSeats] = useState([]);
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    axios.get(`/api/v1/ext/tournaments/${id}/teams/${teamId}`).then(r => {
      const s = r.data.seating.seats.map(x => x.steam_id);
      setTeam(r.data); setSeats(s); setSaved(s);
    }).catch(e => setMsg({ error: true, text: errMsg(e, t('ext.loadFailed', 'Failed to load')) }));
  }, [id, teamId, t]);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await axios.put(`/api/v1/ext/tournaments/${id}/teams/${teamId}/seating`, { seats });
      const s = r.data.seats.map(x => x.steam_id);
      setSeats(s); setSaved(s);
      setMsg({ text: t('extSeating.saved', 'Seating saved') });
    } catch (e) { setMsg({ error: true, text: errMsg(e, t('ext.saveFailed', 'Could not save')) }); }
    finally { setBusy(false); }
  };

  if (!team) return <div style={sub}>{msg ? msg.text : t('common.loading', 'Loading...')}</div>;
  const dirty = JSON.stringify(seats) !== JSON.stringify(saved);

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '1.25rem', paddingBottom: '0.75rem' }}>
        <Link to={`/admin/tournaments/${id}`} style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><Icon d={ICONS.back} /> {t('extTeams.backToTournament', 'Back to the tournament')}</Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.35rem', flexWrap: 'wrap' }}>
          <h1 style={h1}>{t('extSeating.teamTitle', 'Stage seating · {{team}}', { team: team.name })}</h1>
          {team.archived && <span style={pill(RGB.gray)}>{t('ext.archived', 'Archived')}</span>}
          <Link to={`/admin/tournaments/${id}/teams/${teamId}/edit`} style={{ ...btn(RGB.gray), textDecoration: 'none', marginLeft: 'auto' }}><Icon d={ICONS.edit} /> {t('extTeams.editPlayers', 'Edit players')}</Link>
        </div>
      </div>
      <div style={card}>
        <h2 style={h2}>{t('extSeating.teamDefault', 'Default seating')}</h2>
        <p style={sub}>{t('extSeating.teamDefaultHint', 'Seen from the crowd: seat 1 is on the crowd’s left. This is the starting point for every match of this team; seating changed for a single match never changes it. The stage display boards read it from the API.')}</p>
        <div style={{ background: S.bg, border: `1px solid ${S.line}`, borderRadius: 12, padding: '0.4rem' }}>
          <TeamStage teamName={team.name} tint={TEAM_TINT.team1} players={team.players} seats={seats} onChange={setSeats} readOnly={team.archived} />
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', justifyContent: 'flex-end', marginTop: '0.9rem', flexWrap: 'wrap' }}>
          {msg && <span style={{ fontSize: '0.8rem', color: msg.error ? 'var(--accent-red)' : 'var(--accent-green)', marginRight: 'auto' }}>{msg.text}</span>}
          {dirty && <button type="button" style={btn(RGB.gray)} onClick={() => setSeats(saved)}>{t('extSeating.discard', 'Discard changes')}</button>}
          {!team.archived && <button type="button" disabled={busy || !dirty} style={{ ...btn(RGB.green, true), opacity: dirty ? 1 : 0.5 }} onClick={save}>{busy ? t('common.loading', 'Loading...') : t('extSeating.saveSeating', 'Save seating')}</button>}
          <Link to={`/admin/tournaments/${id}`} style={{ ...btn(RGB.blue), textDecoration: 'none' }}>{t('extSeating.done', 'Done')}</Link>
        </div>
      </div>
    </div>
  );
}
